<template>
  <div>
    <base-header
      type="gradient-danger"
      class="pb-6 pb-8 pt-5 pt-md-8"
    ></base-header>
    <div class="container-fluid mt--9">
      <div class="row">
        <div class="col-12 mb-4">
          <div class="card shadow">
            <div class="card-header border-0 d-flex align-items-center">
              <h3 class="mb-0">Solicitudes de acceso temporal</h3>
              <div class="ml-auto access-filters">
                <button
                  v-for="filter in filters"
                  :key="filter.value"
                  type="button"
                  class="btn btn-sm access-filter"
                  :class="{ 'access-filter--active': filter.value === status }"
                  @click="status = filter.value"
                >
                  {{ filter.label }}
                  <span v-if="filter.count" class="access-filter-count">{{
                    filter.count
                  }}</span>
                </button>
              </div>
            </div>
            <div v-if="error" class="alert alert-danger text-sm mx-4 mb-3">
              {{ error }}
            </div>
            <div class="table-responsive">
              <base-table
                thead-classes="thead-light"
                :data="filteredRequests"
                class="align-items-center"
              >
                <template slot="columns">
                  <th>Usuario</th>
                  <th>Módulo</th>
                  <th>Motivo</th>
                  <th>Solicitada</th>
                  <th class="text-right">Acciones</th>
                </template>

                <template slot-scope="{ row }">
                  <td>
                    <span class="font-weight-bold text-sm">{{
                      row.name || row.email
                    }}</span>
                    <br />
                    <span class="text-muted text-xs">{{ row.email }}</span>
                  </td>
                  <td class="text-sm">{{ row.module }}</td>
                  <td class="access-reason text-sm">
                    {{ row.reason }}
                    <br v-if="row.note" />
                    <span v-if="row.note" class="text-muted text-xs"
                      >Nota: {{ row.note }}</span
                    >
                  </td>
                  <td class="text-sm">{{ formatDate(row.requestedAt) }}</td>
                  <td class="text-right">
                    <template v-if="row.status === 'pending'">
                      <base-button
                        size="sm"
                        type="success"
                        icon="fa fa-check"
                        :disabled="saving"
                        @click="openApprove(row)"
                      >
                        Aprobar
                      </base-button>
                      <base-button
                        size="sm"
                        type="danger"
                        icon="fa fa-times"
                        :disabled="saving"
                        @click="openReject(row)"
                      >
                        Rechazar
                      </base-button>
                    </template>
                    <badge v-else-if="row.status === 'approved'" type="success">
                      Aprobada hasta {{ formatDateTime(row.expiresAt) }}
                    </badge>
                    <badge v-else type="danger">Rechazada</badge>
                  </td>
                </template>
              </base-table>
              <p
                v-if="loading"
                class="text-muted text-sm text-center py-4 mb-0"
              >
                Cargando…
              </p>
              <p
                v-else-if="!filteredRequests.length"
                class="text-muted text-sm text-center py-4 mb-0"
              >
                No hay solicitudes {{ statusLabel }}.
              </p>
            </div>
          </div>
        </div>

        <div class="col-12">
          <div class="card shadow">
            <div class="card-header border-0">
              <h3 class="mb-0">Accesos vigentes</h3>
            </div>
            <div class="table-responsive">
              <base-table thead-classes="thead-light" :data="accesses">
                <template slot="columns">
                  <th>Usuario</th>
                  <th>Módulo</th>
                  <th>Vence</th>
                  <th class="text-right">Acciones</th>
                </template>

                <template slot-scope="{ row }">
                  <td>
                    <span class="font-weight-bold text-sm">{{
                      row.name || row.email
                    }}</span>
                    <br />
                    <span class="text-muted text-xs">{{ row.email }}</span>
                  </td>
                  <td class="text-sm">{{ moduleName(row.route) }}</td>
                  <td class="text-sm">
                    {{ formatDateTime(row.expiresAt) }}
                    <badge
                      v-if="hoursLeft(row.expiresAt) <= 72"
                      type="warning"
                      class="ml-2"
                    >
                      {{ timeLeft(row.expiresAt) }}
                    </badge>
                  </td>
                  <td class="text-right">
                    <base-button
                      size="sm"
                      type="primary"
                      outline
                      icon="fa fa-calendar-plus"
                      :disabled="saving"
                      @click="openExtend(row)"
                    >
                      Extender
                    </base-button>
                    <base-button
                      size="sm"
                      type="danger"
                      outline
                      icon="fa fa-ban"
                      :disabled="saving"
                      @click="revoke(row)"
                    >
                      Revocar
                    </base-button>
                  </td>
                </template>
              </base-table>
              <p
                v-if="loading"
                class="text-muted text-sm text-center py-4 mb-0"
              >
                Cargando…
              </p>
              <p
                v-else-if="!accesses.length"
                class="text-muted text-sm text-center py-4 mb-0"
              >
                Nadie tiene acceso temporal.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <b-modal
      v-model="approveOpen"
      title="Aprobar acceso temporal"
      title-tag="h3"
      ok-title="Aprobar"
      ok-variant="success"
      cancel-title="Cancelar"
      body-class="pt-0"
      centered
      :ok-disabled="hours < 1"
      @ok="confirmApprove"
    >
      <p v-if="selected" class="text-sm mb-3">
        <strong>{{ selected.name || selected.email }}</strong> pide acceso a
        <strong>{{ selected.module }}</strong> en la consola anterior.
      </p>
      <p class="text-xs font-weight-bold mb-2">Vigencia</p>
      <div class="access-duration mb-3">
        <input
          v-model.number="amount"
          type="number"
          min="1"
          class="form-control"
        />
        <select v-model="unit" class="form-control">
          <option value="hours">horas</option>
          <option value="days">días</option>
        </select>
      </div>
      <p v-if="approvedUntil" class="text-sm mb-3">
        Vence el: <strong>{{ formatDateTime(approvedUntil) }}</strong>
      </p>
      <p class="text-xs font-weight-bold mb-2">
        Nota para el usuario (opcional)
      </p>
      <textarea
        v-model.trim="note"
        class="form-control"
        rows="2"
        placeholder="Por ejemplo: mientras se corrige el reporte en la nueva consola"
      ></textarea>
    </b-modal>

    <b-modal
      v-model="rejectOpen"
      title="Rechazar solicitud"
      title-tag="h3"
      ok-title="Rechazar"
      ok-variant="danger"
      cancel-title="Cancelar"
      body-class="pt-0"
      centered
      :ok-disabled="!note"
      @ok="confirmReject"
    >
      <p v-if="selected" class="text-sm mb-3">
        <strong>{{ selected.name || selected.email }}</strong> pide
        <strong>{{ selected.module }}</strong> en la consola anterior.
      </p>
      <p class="text-xs font-weight-bold mb-2">Motivo del rechazo</p>
      <textarea
        v-model.trim="note"
        class="form-control"
        rows="3"
        placeholder="El usuario verá este mensaje"
      ></textarea>
    </b-modal>

    <b-modal
      v-model="extendOpen"
      title="Extender acceso temporal"
      title-tag="h3"
      ok-title="Extender"
      ok-variant="primary"
      cancel-title="Cancelar"
      body-class="pt-0"
      centered
      :ok-disabled="hours < 1"
      @ok="confirmExtend"
    >
      <p v-if="selected" class="text-sm mb-3">
        <strong>{{ selected.name || selected.email }}</strong> tiene
        <strong>{{ moduleName(selected.route) }}</strong> hasta el
        <strong>{{ formatDateTime(selected.expiresAt) }}</strong
        >.
      </p>
      <p class="text-xs font-weight-bold mb-2">Extender por</p>
      <div class="access-duration">
        <input
          v-model.number="amount"
          type="number"
          min="1"
          class="form-control"
        />
        <select v-model="unit" class="form-control">
          <option value="hours">horas</option>
          <option value="days">días</option>
        </select>
      </div>
      <p v-if="extendedUntil" class="text-sm mt-3 mb-0">
        Nuevo vencimiento: <strong>{{ formatDateTime(extendedUntil) }}</strong>
      </p>
    </b-modal>
  </div>
</template>

<script>
import { getAuth } from "firebase/auth";
import {
  listRequests,
  listAccesses,
  extendAccess,
  revokeAccess,
  approveRequest,
  rejectRequest,
} from "@/legacyAccess";

export default {
  name: "TemporaryAccess",
  data() {
    return {
      note: "",
      error: "",
      status: "pending",
      saving: false,
      loading: true,
      selected: null,
      amount: 1,
      unit: "days",
      approveOpen: false,
      rejectOpen: false,
      extendOpen: false,
      requests: [],
      accesses: [],
    };
  },
  computed: {
    hours() {
      const amount = Number(this.amount) || 0;
      return this.unit === "days" ? amount * 24 : amount;
    },
    extendedUntil() {
      if (!this.selected || !this.selected.expiresAt) return null;
      return this.addHours(this.selected.expiresAt);
    },
    approvedUntil() {
      if (!this.selected) return null;
      const current = this.accesses.find(
        (access) => access.email === this.selected.email && access.route === this.selected.route
      );
      return this.addHours(current ? current.expiresAt : null);
    },
    filters() {
      return [
        { value: "pending", label: "Pendientes", count: this.count("pending") },
        { value: "approved", label: "Aprobadas", count: 0 },
        { value: "rejected", label: "Rechazadas", count: 0 },
      ];
    },
    filteredRequests() {
      return this.requests.filter((request) => request.status === this.status);
    },
    statusLabel() {
      return this.filters
        .find((filter) => filter.value === this.status)
        .label.toLowerCase();
    },
    reviewer() {
      const user = getAuth().currentUser;
      return user ? user.email : "";
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.error = "";
      this.loading = true;
      try {
        [this.requests, this.accesses] = await Promise.all([
          listRequests(),
          listAccesses(),
        ]);
      } catch (error) {
        console.error("Error al cargar los accesos temporales: ", error);
        this.error = "No se pudieron cargar las solicitudes.";
      } finally {
        this.loading = false;
      }
    },
    async run(action, failure) {
      this.error = "";
      this.saving = true;
      try {
        await action();
        await this.load();
      } catch (error) {
        console.error(failure, error);
        this.error = failure;
      } finally {
        this.saving = false;
      }
    },
    count(status) {
      return this.requests.filter((request) => request.status === status)
        .length;
    },
    openApprove(request) {
      this.note = "";
      this.amount = 1;
      this.unit = "days";
      this.selected = request;
      this.approveOpen = true;
    },
    openReject(request) {
      this.note = "";
      this.selected = request;
      this.rejectOpen = true;
    },
    openExtend(access) {
      this.amount = 1;
      this.unit = "days";
      this.selected = access;
      this.extendOpen = true;
    },
    confirmApprove() {
      const request = this.selected;
      const { hours, note, reviewer } = this;
      this.run(
        () => approveRequest(request, { hours, note, reviewer }),
        "No se pudo aprobar la solicitud.",
      );
    },
    confirmReject() {
      const request = this.selected;
      const { note, reviewer } = this;
      this.run(
        () => rejectRequest(request, { note, reviewer }),
        "No se pudo rechazar la solicitud.",
      );
    },
    confirmExtend() {
      const access = this.selected;
      const { hours, reviewer } = this;
      this.run(
        () => extendAccess(access, { hours, reviewer }),
        "No se pudo extender el acceso.",
      );
    },
    async revoke(access) {
      const result = await this.$swal.fire({
        title: "Revocar acceso",
        text: `¿Quitar a ${access.name ||
          access.email} el acceso a ${this.moduleName(access.route)}?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Revocar",
        cancelButtonText: "Cancelar",
        reverseButtons: true,
      });
      if (!result.isConfirmed) return;
      this.run(() => revokeAccess(access), "No se pudo revocar el acceso.");
    },
    addHours(expiresAt) {
      if (this.hours < 1) return null;
      const now = new Date();
      const base = expiresAt && expiresAt > now ? expiresAt : now;
      return new Date(base.getTime() + this.hours * 3600000);
    },
    moduleName(path) {
      const { route } = this.$router.resolve(path);
      return route.meta.legacy ? route.meta.legacy.name : path;
    },
    formatDate(date) {
      if (!date) return "";
      return date.toLocaleDateString("es-MX", {
        day: "numeric",
        month: "long",
      });
    },
    formatDateTime(date) {
      if (!date) return "";
      return date.toLocaleString("es-MX", {
        day: "numeric",
        month: "long",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    hoursLeft(date) {
      if (!date) return 0;
      return Math.ceil((date - new Date()) / 3600000);
    },
    timeLeft(date) {
      const hours = this.hoursLeft(date);
      if (hours < 24) return `${hours} ${hours === 1 ? "hora" : "horas"}`;
      const days = Math.ceil(hours / 24);
      return `${days} ${days === 1 ? "día" : "días"}`;
    },
  },
};
</script>

<style scoped>
.access-filters {
  display: flex;
  gap: 6px;
}
.access-filter {
  border-radius: 50px;
  border: 1px solid #e9ecef;
  background: #fff;
  color: #525f7f;
  box-shadow: none;
}
.access-filter--active {
  background: #32325d;
  border-color: #32325d;
  color: #fff;
}
.access-filter-count {
  display: inline-block;
  min-width: 18px;
  margin-left: 4px;
  padding: 0 5px;
  border-radius: 50px;
  background: #f5365c;
  color: #fff;
  font-size: 0.65rem;
  line-height: 18px;
}
.access-reason {
  max-width: 360px;
  white-space: normal;
}
.access-duration {
  display: flex;
  gap: 6px;
  max-width: 240px;
}
</style>
